// src/pages/HomePage.tsx
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AccountBalance as AccountBalanceIcon,
  Groups as GroupsIcon,
  ReceiptLong as ReceiptLongIcon,
  ArrowForward as ArrowForwardIcon,
  CreditCard as CreditCardIcon,
  ShowChart as ShowChartIcon,
  Notifications as NotificationsIcon,
} from "@mui/icons-material";

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();

  const features = [
    {
      title: "Split Expenses Fairly",
      description:
        "Easily split bills, rent, and other expenses with roommates, friends, or travel companions.",
      icon: <ReceiptLongIcon fontSize="large" color="primary" />,
    },
    {
      title: "Group Management",
      description:
        "Create groups for roommates, trips, or events. Track who owes what within each group.",
      icon: <GroupsIcon fontSize="large" color="primary" />,
    },
    {
      title: "Instant Settlements",
      description:
        "Settle debts with a tap and keep track of your balances in real-time.",
      icon: <CreditCardIcon fontSize="large" color="primary" />,
    },
  ];

  const additionalFeatures = [
    {
      title: "Smart Expense Tracking",
      description:
        "Categorize expenses, tag recurring bills, and get insights into your spending patterns.",
      icon: (
        <ShowChartIcon
          fontSize="large"
          sx={{ color: theme.palette.secondary.main }}
        />
      ),
    },
    {
      title: "Debt Simplification",
      description:
        "Our algorithm simplifies multiple debts so you make fewer transactions when settling up.",
      icon: (
        <AccountBalanceIcon
          fontSize="large"
          sx={{ color: theme.palette.secondary.main }}
        />
      ),
    },
    {
      title: "Reminders & Notifications",
      description:
        "Get notified about pending settlements and send gentle reminders to friends.",
      icon: (
        <NotificationsIcon
          fontSize="large"
          sx={{ color: theme.palette.secondary.main }}
        />
      ),
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          background: `linear-gradient(120deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "white",
          mb: 4,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "65vh",
          display: "flex",
          alignItems: "center",
          borderRadius: 0,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            right: 0,
            left: 0,
            backgroundColor: "rgba(0,0,0,.2)",
            backgroundImage: "url('/pattern.svg')",
            backgroundRepeat: "repeat",
            backgroundSize: "300px",
            opacity: 0.1,
          }}
        />
        <Container maxWidth="lg" sx={{ position: "relative", py: 8 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                component="h1"
                variant="h2"
                color="inherit"
                gutterBottom
                sx={{ fontWeight: 700 }}
              >
                Split Bills & Expenses <br />
                With{" "}
                <span style={{ color: theme.palette.secondary.dark }}>
                  Ease
                </span>
              </Typography>
              <Typography
                variant="h5"
                color="inherit"
                paragraph
                sx={{ opacity: 0.9 }}
              >
                Tab makes it simple to share expenses with friends and family,
                track balances, and settle up without the stress.
              </Typography>
              {user ? (
                <Button
                  component={RouterLink}
                  to="/groups"
                  variant="contained"
                  color="secondary"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ mt: 2, px: 4, py: 1.5, borderRadius: 2 }}
                >
                  Go to Dashboard
                </Button>
              ) : (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  sx={{ mt: 4 }}
                >
                  <Button
                    component={RouterLink}
                    to="/register"
                    variant="contained"
                    color="secondary"
                    size="large"
                    sx={{ px: 4, py: 1.5, borderRadius: 2 }}
                  >
                    Sign Up Free
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/login"
                    variant="outlined"
                    size="large"
                    sx={{
                      px: 4,
                      py: 1.5,
                      bgcolor: "rgba(255,255,255,0.1)",
                      color: "white",
                      borderColor: "white",
                      borderRadius: 2,
                    }}
                  >
                    Login
                  </Button>
                </Stack>
              )}
            </Box>
            <Box
              sx={{
                flex: 1,
                display: { xs: "none", md: "flex" },
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box
                component="img"
                src="/hero-image.png"
                alt="Tab expense sharing app"
                sx={{
                  width: "100%",
                  maxWidth: 500,
                  height: "auto",
                  filter: "drop-shadow(0px 8px 16px rgba(0,0,0,0.15))",
                  borderRadius: 4,
                }}
              />
            </Box>
          </Box>
        </Container>
      </Paper>

      {/* Primary Features Section */}
      <Container sx={{ py: 6 }}>
        <Typography
          component="h2"
          variant="h3"
          align="center"
          color="text.primary"
          gutterBottom
          sx={{ fontWeight: 600 }}
        >
          Split Expenses Without Drama
        </Typography>
        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          paragraph
          sx={{ maxWidth: 800, mx: "auto", mb: 6 }}
        >
          Tab makes it easy to track shared expenses, settle debts, and keep
          everyone on the same page.
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
          {features.map((feature, index) => (
            <Box key={index} sx={{ flex: { xs: "1 1 100%", md: "1 1 30%" } }}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition:
                    "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                  borderRadius: 3,
                  overflow: "hidden",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: 8,
                  },
                }}
              >
                <Box
                  sx={{
                    height: 8,
                    bgcolor: "primary.main",
                  }}
                />
                <CardContent sx={{ flexGrow: 1, p: 4 }}>
                  <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                  <Typography
                    gutterBottom
                    variant="h5"
                    component="h3"
                    sx={{ fontWeight: 600 }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      </Container>

      {/* How It Works Section */}
      <Box sx={{ bgcolor: "grey.50", py: 8 }}>
        <Container>
          <Typography
            component="h2"
            variant="h3"
            align="center"
            color="text.primary"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            How Tab Works
          </Typography>
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            paragraph
            sx={{ maxWidth: 800, mx: "auto", mb: 6 }}
          >
            Three simple steps to hassle-free expense sharing
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            <Box
              sx={{
                flex: { xs: "1 1 100%", md: "1 1 30%" },
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                  fontSize: "2rem",
                  fontWeight: "bold",
                }}
              >
                1
              </Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Add Expenses
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Enter any shared expenses as they come up. Split them equally or
                customize amounts.
              </Typography>
            </Box>
            <Box
              sx={{
                flex: { xs: "1 1 100%", md: "1 1 30%" },
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                  fontSize: "2rem",
                  fontWeight: "bold",
                }}
              >
                2
              </Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Track Balances
              </Typography>
              <Typography variant="body1" color="text.secondary">
                See who owes what at a glance. Tab calculates and simplifies all
                balances automatically.
              </Typography>
            </Box>
            <Box
              sx={{
                flex: { xs: "1 1 100%", md: "1 1 30%" },
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                  fontSize: "2rem",
                  fontWeight: "bold",
                }}
              >
                3
              </Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Settle Up
              </Typography>
              <Typography variant="body1" color="text.secondary">
                When it's time to pay up, record payments in the app or use
                integrated payment options.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Secondary Features */}
      <Container sx={{ py: 8 }}>
        <Typography
          component="h2"
          variant="h3"
          align="center"
          color="text.primary"
          gutterBottom
          sx={{ fontWeight: 600 }}
        >
          More Powerful Features
        </Typography>
        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          paragraph
          sx={{ maxWidth: 800, mx: "auto", mb: 6 }}
        >
          Tab is designed to make expense sharing with friends effortless and
          stress-free
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
          {additionalFeatures.map((feature, index) => (
            <Box key={index} sx={{ flex: { xs: "1 1 100%", md: "1 1 30%" } }}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition:
                    "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                  borderRadius: 3,
                  overflow: "hidden",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: 8,
                  },
                }}
              >
                <Box
                  sx={{
                    height: 8,
                    bgcolor: "secondary.main",
                  }}
                />
                <CardContent sx={{ flexGrow: 1, p: 4 }}>
                  <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                  <Typography
                    gutterBottom
                    variant="h5"
                    component="h3"
                    sx={{ fontWeight: 600 }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Call to Action */}
      {!user && (
        <Paper
          sx={{
            background: `linear-gradient(120deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
            py: 8,
            px: 4,
            mt: 6,
            textAlign: "center",
            borderRadius: 0,
            color: "white",
          }}
        >
          <Container maxWidth="md">
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
              Ready to simplify expense sharing?
            </Typography>
            <Typography variant="h6" paragraph sx={{ mb: 4, opacity: 0.9 }}>
              Join thousands of users already saving time and avoiding awkward
              money conversations.
            </Typography>
            <Button
              component={RouterLink}
              to="/register"
              variant="contained"
              color="primary"
              size="large"
              sx={{
                mt: 2,
                px: 6,
                py: 1.5,
                borderRadius: 2,
                bgcolor: "white",
                color: "secondary.dark",
              }}
            >
              Create an Account
            </Button>
          </Container>
        </Paper>
      )}

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 6,
          px: 2,
          mt: "auto",
          backgroundColor: (theme) =>
            theme.palette.mode === "light"
              ? theme.palette.grey[900]
              : theme.palette.grey[800],
          color: "white",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            <Box
              sx={{
                flex: { xs: "1 1 100%", sm: "1 1 100%", md: "1 1 30%" },
                mb: { xs: 3, md: 0 },
              }}
            >
              <Typography variant="h6" gutterBottom>
                Tab
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
                The smart way to share expenses with friends, roommates, and
                family.
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 4, bgcolor: "rgba(255,255,255,0.1)" }} />
          <Typography variant="body2" align="center" sx={{ opacity: 0.7 }}>
            © {new Date().getFullYear()} Tab Inc. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
